import React, { useRef, useState } from 'react';
import Loader from './ui/Loader';
import { API_BASE } from '../apiBase';

interface LeadMagnetOptions {
  includeImages: boolean;
  style: 'modern' | 'minimal' | 'elegant' | 'bold';
  colorScheme: 'blue' | 'purple' | 'green' | 'orange' | 'neutral';
  layoutImages?: string[];
  useEmojis?: boolean;
  documentType: 'standard' | 'workbook' | 'reading';
}

interface PageData {
  pageNumber: number;
  title: string;
  type: string;
  html: string;
  text: string;
  imagePrompt: string | null;
  imageUrl: string | null;
  imageBase64: string | null;
}

interface LeadMagnetResult {
  success: boolean;
  title: string;
  pages: PageData[];
  totalPages: number;
  metadata: {
    style: string;
    colorScheme: string;
    includeImages: boolean;
    generatedAt: string;
  };
  pdfHtml?: string;
  pdfBase64?: string;
  error?: string;
}

const LeadMagnetMaker: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [options, setOptions] = useState<LeadMagnetOptions>({
    includeImages: true,
    style: 'modern',
    colorScheme: 'blue',
    useEmojis: true,
    documentType: 'standard'
  });
  const [layoutImages, setLayoutImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<LeadMagnetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState(0);

  const designLocked = layoutImages.length > 0;

  const handleGenerate = async () => {
    if (!inputText.trim()) {
      setError('Please enter some content for your lead magnet');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/generate-lead-magnet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Required for cross-origin session cookies
        body: JSON.stringify({
          text: inputText,
          options: {
            ...options,
            layoutImages
          }
        }),
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        setSelectedPage(0);
      } else {
        setError(data.error || 'Failed to generate lead magnet');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!result?.pdfHtml) {
      setError('PDF HTML not available. Please regenerate your lead magnet.');
      return;
    }

    // Open in new window for print-to-PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow pop-ups to download PDF');
      return;
    }

    printWindow.document.write(result.pdfHtml);
    printWindow.document.close();

    // Wait for content to load, then trigger print dialog
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const handleDownloadHtml = () => {
    if (!result?.pdfHtml) return;

    const blob = new Blob([result.pdfHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadJson = () => {
    if (!result) return;

    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLayoutImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const imagePromises = Array.from(files).map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then((images) => {
      setLayoutImages(prev => [...prev, ...images]);
    });
  };

  const removeLayoutImage = (index: number) => {
    setLayoutImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

    const imagePromises = imageFiles.map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve(event.target?.result as string);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then((images) => {
      setLayoutImages(prev => [...prev, ...images]);
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
        <div className="mb-6">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 text-transparent bg-clip-text mb-2">
            📚 Lead Magnet Maker
          </h2>
          <p className="text-muted-foreground">
            Create professional lead magnets with AI-designed pages, images, and styling
          </p>
        </div>

        {/* Input Section */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Content for Your Lead Magnet
            </label>
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter the content you want to turn into a lead magnet. This could be tips, a guide, checklist, or any valuable information..."
              className="w-full h-48 px-4 py-3 bg-background border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-foreground placeholder:text-muted-foreground"
            />
            <p className="mt-1 text-sm text-muted-foreground">
              {inputText.length} characters
            </p>
          </div>

          {/* Layout Images Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-2">
              Layout Design Images (Optional)
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Upload or drag & drop example layouts/designs for AI to analyze and replicate the style
            </p>

            {/* Drag and Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-primary/30 rounded-lg p-8 text-center bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
            >
              <div className="flex flex-col items-center">
                <svg className="w-12 h-12 text-primary/50 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-foreground mb-2">
                  <span className="font-semibold text-primary">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">PNG, JPG, WebP up to 10MB</p>
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleLayoutImageUpload}
                className="hidden"
                ref={fileInputRef}
              />
            </div>

            {layoutImages.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-foreground mb-2">
                  Uploaded Images ({layoutImages.length})
                </p>
                <div className="flex flex-wrap gap-3">
                  {layoutImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img}
                        alt={`Layout ${index + 1}`}
                        className="w-24 h-24 object-cover rounded-lg border-2 border-primary/50 shadow-sm group-hover:border-primary transition-colors"
                      />
                      <button
                        onClick={() => removeLayoutImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 shadow-md transition-colors"
                        title="Remove image"
                      >
                        ×
                      </button>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-opacity" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Include Images Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
              <div>
                <label className="text-sm font-semibold text-foreground">Include Images</label>
                <p className="text-xs text-muted-foreground">AI-generated visuals</p>
              </div>
              <button
                onClick={() => setOptions({ ...options, includeImages: !options.includeImages })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${options.includeImages ? 'bg-primary' : 'bg-muted'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${options.includeImages ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {/* Use Emojis Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
              <div>
                <label className="text-sm font-semibold text-foreground">Use Emojis/Icons</label>
                <p className="text-xs text-muted-foreground">When no images</p>
              </div>
              <button
                onClick={() => setOptions({ ...options, useEmojis: !options.useEmojis })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${options.useEmojis ? 'bg-primary' : 'bg-muted'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-background transition-transform ${options.useEmojis ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Document Type Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-foreground mb-2">
                Document Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: 'standard', title: 'Lead Magnet', description: 'Balanced tips + visuals' },
                  { value: 'workbook', title: 'Workbook', description: 'Interactive prompts & writing spaces' },
                  { value: 'reading', title: 'Reading PDF', description: 'Narrative-rich, minimal exercises' }
                ].map(({ value, title, description }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setOptions({ ...options, documentType: value as LeadMagnetOptions['documentType'] })}
                    className={`text-left p-4 rounded-lg border transition-all ${options.documentType === value
                      ? 'border-primary bg-primary/10 text-primary shadow-sm'
                      : 'border-border bg-card hover:border-primary/50 hover:bg-muted/50 text-foreground'
                      }`}
                  >
                    <p className="text-sm font-semibold">{title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{description}</p>
                  </button>
                ))}
              </div>
            </div>

            {designLocked && (
              <div className="md:col-span-2 p-3 bg-primary/10 border border-primary/20 rounded-lg text-sm text-primary">
                Layout images detected. Design style and color scheme will be matched to the uploaded layouts automatically.
              </div>
            )}

            {/* Style Selection */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Design Style
              </label>
              <select
                value={options.style}
                onChange={(e) => setOptions({ ...options, style: e.target.value as LeadMagnetOptions['style'] })}
                disabled={designLocked}
                className={`w-full px-3 py-2 bg-background border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground ${designLocked ? 'bg-muted text-muted-foreground border-border cursor-not-allowed' : 'border-border'}`}
              >
                <option value="modern">Modern</option>
                <option value="minimal">Minimal</option>
                <option value="elegant">Elegant</option>
                <option value="bold">Bold</option>
              </select>
            </div>

            {/* Color Scheme Selection */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Color Scheme
              </label>
              <select
                value={options.colorScheme}
                onChange={(e) => setOptions({ ...options, colorScheme: e.target.value as LeadMagnetOptions['colorScheme'] })}
                disabled={designLocked}
                className={`w-full px-3 py-2 bg-background border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground ${designLocked ? 'bg-muted text-muted-foreground border-border cursor-not-allowed' : 'border-border'}`}
              >
                <option value="blue">Blue</option>
                <option value="purple">Purple</option>
                <option value="green">Green</option>
                <option value="orange">Orange</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !inputText.trim()}
            className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold py-3 px-6 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
          >
            {isGenerating ? 'Generating Lead Magnet...' : '✨ Generate Lead Magnet'}
          </button>
        </div>

        {/* Loading State */}
        {isGenerating && (
          <div className="mt-8 text-center">
            <Loader />
            <p className="mt-4 text-slate-600">Creating your lead magnet with AI...</p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">❌ {error}</p>
          </div>
        )}

        {/* Results Display */}
        {result && result.success && (
          <div className="mt-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-foreground">{result.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {result.totalPages} pages • {result.metadata.style} style • {result.metadata.colorScheme} colors
                  {result.metadata.includeImages && ' • With images'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadPdf}
                  className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors font-semibold text-sm flex items-center gap-2"
                  title="Open print dialog to save as PDF"
                >
                  📄 Print to PDF
                </button>
                <button
                  onClick={handleDownloadHtml}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors font-semibold text-sm"
                >
                  💾 Download HTML
                </button>
                <button
                  onClick={handleDownloadJson}
                  className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors font-semibold text-sm"
                >
                  📊 Download JSON
                </button>
              </div>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {result.pages.map((page, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedPage(index)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${selectedPage === index
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                >
                  Page {page.pageNumber}: {page.title}
                </button>
              ))}
            </div>

            {/* Page Preview */}
            {result.pages[selectedPage] && (
              <div className="border border-slate-200 rounded-lg overflow-hidden" style={{ maxWidth: '100%' }}>
                <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-slate-700">
                        Page {result.pages[selectedPage].pageNumber} - {result.pages[selectedPage].title}
                      </span>
                      <span className="ml-3 text-xs text-slate-500 uppercase">
                        {result.pages[selectedPage].type}
                      </span>
                    </div>
                    {result.pages[selectedPage].imagePrompt && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        🎨 Has Image
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6 bg-muted/50 flex justify-center">
                  <div style={{
                    width: '105mm',
                    height: '148.5mm',
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div
                      className="prose max-w-none"
                      style={{
                        width: '210mm',
                        height: '297mm',
                        overflow: 'hidden',
                        padding: '20mm',
                        boxSizing: 'border-box',
                        backgroundColor: 'white',
                        transform: 'scale(0.5)',
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                      dangerouslySetInnerHTML={{ __html: result.pages[selectedPage].html }}
                    />
                  </div>
                  {result.pages[selectedPage].imageBase64 && (
                    <div className="mt-4">
                      <img
                        src={`data:image/png;base64,${result.pages[selectedPage].imageBase64}`}
                        alt={result.pages[selectedPage].title}
                        className="max-w-full h-auto rounded-lg shadow-md"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Page Text Content */}
            {result.pages[selectedPage] && (
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-2">Plain Text Content:</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {result.pages[selectedPage].text}
                </p>
                {result.pages[selectedPage].imagePrompt && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-1">Image Prompt:</h4>
                    <p className="text-sm text-muted-foreground italic">
                      {result.pages[selectedPage].imagePrompt}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="mt-6 bg-primary/5 border border-primary/10 rounded-lg p-4">
        <h4 className="font-semibold text-primary mb-2">💡 How it works:</h4>
        <ul className="text-sm text-primary/80 space-y-1">
          <li>• <strong>Step 1:</strong> Gemini analyzes your content and structures it into pages</li>
          <li>• <strong>Step 2:</strong> Each page is generated with custom HTML, text, and image prompts</li>
          <li>• <strong>Step 3:</strong> Images are generated using Gemini Image model (if enabled)</li>
          <li>• <strong>Step 4:</strong> Pages maintain context from previous pages for coherence</li>
          <li>• <strong>Step 5:</strong> Download as HTML (print to PDF) or JSON for further processing</li>
        </ul>
      </div>
    </div>
  );
};

export default LeadMagnetMaker;
